import { Key, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';

import {
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogHeader,
  Flex,
  IconButton,
  Menu,
  MenuItem,
  reactIcons as Icons,
  Select,
  SelectItem,
  Separator,
  Tag,
  Typography,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

import { viewExamples } from './constants';

const styles = {
  allButton: {
    flexDirection: 'column',
    width: '100%',
    '@media (max-width: 600px)': {
      flexDirection: 'row-reverse',
      gap: '$medium',
    },
  },
  allCard: {
    '@media (max-width: 600px)': {
      flexGrow: 1,
    },
  },
  button: {
    '@media (max-width: 719px)': {
      display: 'none',
    },
  },
  content: {
    bgColor: '$palette-grey-50',
    overflow: 'scroll',
    padding: '$large',
    width: '100%',
  },
  dialog: {
    minWidth: 800,
    padding: 0,
    '@media (max-width: 900px)': {
      minWidth: 600,
    },
    '@media (max-width: 719px)': {
      minWidth: 400,
    },
  },
  iconButton: {
    display: 'none',
    '@media (max-width: 719px)': {
      display: 'block',
    },
  },
  limit: {
    minWidth: 'auto',
    flexBasis: 1,
    flexGrow: 1,
    flexShrink: 1,
    '@media (max-width: 600px)': { '&:nth-child(n+1)': { display: 'none' } },
    '@media (max-width: 800px)': { '&:nth-child(n+2)': { display: 'none' } },
    '@media (max-width: 1200px)': { '&:nth-child(n+3)': { display: 'none' } },
    '@media (max-width: 1400px)': { '&:nth-child(n+4)': { display: 'none' } },
    '@media (min-width: 1400px)': { '&:nth-child(n+5)': { display: 'none' } },
  },
  menu: {
    display: 'flex',
    '@media (max-width: 719px)': {
      display: 'none',
    },
  },
  select: {
    display: 'none',
    padding: '0 $large',
    backgroundColor: '$background-secondary',
    '@media (max-width: 719px)': {
      display: 'block',
    },
  },
  separator: {
    '@media (max-width: 600px)': { display: 'none' },
  },
};

// TODO translations
const list = [
  'Recommended',
  'Exceptions',
  'Reporting',
  'Ocean',
  'Truckload',
  'Rail',
];

function ViewRecommendations() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState<Key>('Recommended');

  const MenuFoo = useCallback(
    ({ direction, hideButton }) => (
      <Menu
        aria-label={t('core.selectViewCategory')}
        css={{
          ...styles.menu,
          backgroundColor: hideButton ? '$white' : '$palette-grey-50',
          flexDirection: direction,
          padding: hideButton ? '$large' : '0 $large',
        }}
        children={list.map((label) => (
          <MenuItem
            key={label}
            css={{
              textAlign: 'center',
              '& span': {
                color: tab === label ? '$primary-default' : '',
              },
            }}
            aria-label={label}
            isSelected={tab === label}
            label={label}
            onClick={() => setTab(label)}
          />
        ))}
      />
    ),
    [tab, t],
  );

  const SelectFoo = useCallback(
    ({ hideButton }) => (
      <Select
        aria-label={t('core.selectViewCategory')}
        css={{ ...styles.select, padding: hideButton ? '0 $large' : 0 }}
        onSelectionChange={setTab}
        selectedKey={tab}
        children={list.map((opt) => (
          <SelectItem key={opt} aria-label={opt} children={opt} />
        ))}
      />
    ),
    [tab, t],
  );

  const Content = useCallback(
    ({ direction, hideButton }) => (
      <Flex
        gap="medium"
        css={{
          ...styles.content,
          flexDirection: direction,
          height: hideButton ? 400 : '',
        }}
      >
        {
          // TOTO clean up types
          // @ts-expect-error Key should be usable for indexing object
          viewExamples[hideButton ? tab : 'Recommended'].map(
            ({
              description,
              label,
              tags,
              url,
            }: {
              description: string;
              label: string;
              tags: string[];
              url: string;
              hideButton: boolean;
            }) => (
              <Card
                key={label}
                onClick={!hideButton ? () => navigate(url) : undefined}
                css={{
                  flexShrink: 0,
                  minWidth: !hideButton ? 400 : '',
                  ...(!hideButton ? { cursor: 'pointer' } : {}),
                  ...(!hideButton ? styles.limit : {}),
                }}
              >
                {hideButton && (
                  <Flex
                    gap="small"
                    css={{ paddingLeft: '$medium', paddingTop: '$medium' }}
                  >
                    {tags.map((tag) => (
                      <Tag key={tag} children={tag} />
                    ))}
                  </Flex>
                )}
                <Flex
                  justify="between"
                  align="center"
                  css={{ padding: '$medium' }}
                >
                  <Box css={{ paddingRight: '$medium', flexGrow: 1 }}>
                    <Flex justify="between" css={{ marginBottom: '$medium' }}>
                      <Typography
                        as="p"
                        variant="subtextBold"
                        children={label}
                      />
                      {!hideButton && <Tag key={tags[0]} children={tags[0]} />}
                    </Flex>
                    <Typography
                      as="p"
                      variant="caption"
                      children={description}
                    />
                  </Box>
                  {hideButton && (
                    <Button
                      aria-label={t('core.navigateToView', { label })}
                      children={t('core.preview')}
                      onPress={() => navigate(url)}
                      startIcon={<Icons.EyeOpen />}
                      css={styles.button}
                    />
                  )}
                  {hideButton && (
                    <IconButton
                      // TODO fix unit test double aria
                      // aria-label={t('core.navigateToView', { label })}
                      css={styles.iconButton}
                      onPress={() => navigate(url)}
                      children={<Icons.EyeOpen />}
                    />
                  )}
                </Flex>
              </Card>
            ),
          )
        }
        {!hideButton && (
          <>
            <Separator orientation="vertical" css={styles.separator} />
            <Card
              css={{
                height: '100%',
                flexShrink: 0,
                padding: '$large',
                ...styles.allCard,
              }}
            >
              <Button
                aria-label={t('core.openViewRecommendations')}
                css={{ height: '100%', width: '100%' }}
                onPress={() => setIsOpen(true)}
                variant="tertiary"
                children={
                  <Flex align="center" justify="center" css={styles.allButton}>
                    <Icons.ArrowRight />
                    <Typography children="View all" />
                  </Flex>
                }
              />
            </Card>
          </>
        )}
      </Flex>
    ),
    [navigate, tab, t],
  );

  return (
    <Box>
      <Card css={{ bgColor: '$palette-grey-50' }}>
        <Typography
          as="p"
          css={{ padding: '$medium $large 0' }}
          variant="bodyBold"
          children={'Suggested Views'}
        />
        <Content direction="row" />
      </Card>
      <Dialog
        css={styles.dialog}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      >
        <DialogHeader css={{ padding: '$medium $large' }}>
          {t('core.recommendedViews')}
        </DialogHeader>
        <DialogContent data-testid="view-recommendations" css={{ padding: 0 }}>
          <SelectFoo hideButton />
          <Flex>
            <MenuFoo direction="column" hideButton />
            <Content direction="column" hideButton />
          </Flex>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default ViewRecommendations;
